import {useState} from "react";
import {Loader, Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import type {SourceInfo} from "../../../types";
import {ExperimentalLink} from "../../../components/ExperimentalLink";
import {resolveDibHref} from "../../../utils/dibUrls";
import {AccessContext, type AccessEntry} from "../../content/AccessContext";
import {HTMLResource} from "../../content/HTMLResource";
import {ResourceError} from "../../content/ResourceError";
import {ResourceType} from "../../content/types";
import {PanelProvider} from "../../content/panel/PanelContext";
import {SliderPanel} from "../../content/panel/SliderPanel";
import {targetFromSource} from "../citation-preview/useCitationParts";
import {useTopicOpenAnalytics} from "../../content/useTopicOpenAnalytics";
import {noteIslandToPage} from "../../content/topicEntryNote";
import classes from "./TopicIslandContent.module.css";

const documentTypes = new Set([
    ResourceType.html,
    ResourceType.staticHtml,
    ResourceType.skatteAbc,
    ResourceType.dibDocumentFormat,
]);

/** Renders a complete topic as island content, inside the thread. */
export function TopicIslandContent({source, compactToc}: {
    source: SourceInfo;
    compactToc: boolean;
}) {
    const {t} = useTranslation();
    const target = targetFromSource(source);
    const resourceId = target?.resourceId;
    const [accessEntry, setAccessEntry] = useState<AccessEntry | null>(null);
    const {currentData: resourceType, isFetching, isError, error} =
        dibAppApi.endpoints.getResourceTypeId.useQuery(
            {topicId: resourceId ?? ""},
            {skip: !resourceId}
        );
    const noAccess = accessEntry?.id === resourceId && !accessEntry?.access;
    useTopicOpenAnalytics({
        topicId: resourceId,
        shown: !!resourceId && accessEntry !== null && accessEntry.id === resourceId && accessEntry.access,
        surface: "island",
        segmentId: target?.segmentId || undefined,
        sectionId: target?.sectionId,
        fallbackSource: "citation",
    });
    const href = resolveDibHref(source.url);

    if (!resourceId || (resourceType && !documentTypes.has(resourceType as ResourceType))) {
        return (
            <div className={classes.status}>
                <Text size="sm">{t("WorkIsland.OpenOnSourcePage")}</Text>
                {href && (
                    <ExperimentalLink href={href} onClick={() => noteIslandToPage(resourceId)}>
                        {t("WorkIsland.OpenFullTopic")}
                    </ExperimentalLink>
                )}
            </div>
        );
    }
    if (isError || noAccess) {
        return <div className={classes.status}><ResourceError error={noAccess ? {status: 403} : error} /></div>;
    }
    if (isFetching || !resourceType) {
        return <div className={classes.status} role="status" aria-label={t("citationPreview.loading")}><Loader size="sm" color="gray" /></div>;
    }

    return (
        <PanelProvider>
            <AccessContext.Provider value={setAccessEntry}>
                <HTMLResource
                    resourceId={resourceId}
                    segmentId={target?.segmentId || undefined}
                    embedded={{compactToc, sectionId: target?.sectionId}}
                />
                <SliderPanel />
            </AccessContext.Provider>
        </PanelProvider>
    );
}
